import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  LayoutGrid, Inbox, Boxes, Sparkles, Star, PenLine, Settings, LogOut,
  Check, X, RefreshCw, Upload, Eye, Trash2, Copy, Loader2, Play, Send,
} from "lucide-react";

const SLUG = "artpart";
const TABS = [
  { id: "overview", label: "Übersicht", icon: LayoutGrid, tid: "admin-tab-overview" },
  { id: "moderation", label: "Moderation", icon: Inbox, tid: "admin-tab-moderation" },
  { id: "inputs", label: "Eingaben", icon: Inbox, tid: "admin-tab-inputs" },
  { id: "clusters", label: "Cluster", icon: Boxes, tid: "admin-tab-clusters" },
  { id: "manifestations", label: "Manifestationen", icon: Sparkles, tid: "admin-tab-manifestations" },
  { id: "reviews", label: "Google Reviews", icon: Star, tid: "admin-tab-reviews" },
  { id: "artist", label: "Artist Input", icon: PenLine, tid: "admin-tab-artist-input" },
  { id: "settings", label: "Einstellungen", icon: Settings, tid: "admin-tab-settings" },
];

export default function AdminDashboard() {
  const { logout } = useAuth();
  const [tab, setTab] = useState("overview");

  return (
    <div style={{ minHeight: "100vh", background: "#0A0A09", display: "flex" }}>
      {/* sidebar */}
      <aside style={sidebar}>
        <div style={{ padding: "22px 20px", borderBottom: "1px solid var(--cs-border)" }}>
          <div className="cs-heading" style={{ fontSize: 13, color: "var(--cs-bone)" }}>COGNITIVE SPACE</div>
          <div className="cs-mono" style={{ fontSize: 9, color: "var(--cs-mute)", marginTop: 4 }}>ART PART · KURATOR</div>
        </div>
        <nav style={{ flex: 1, padding: "12px 0" }}>
          {TABS.map((t) => (
            <button key={t.id} data-testid={t.tid} onClick={() => setTab(t.id)} style={navItem(tab === t.id)}>
              <t.icon size={15} /> <span style={{ fontSize: 12 }}>{t.label}</span>
            </button>
          ))}
        </nav>
        <div style={{ padding: 16, borderTop: "1px solid var(--cs-border)", display: "flex", flexDirection: "column", gap: 8 }}>
          <Link to="/" className="cs-mono" style={{ fontSize: 10, color: "var(--cs-mute)", textDecoration: "none" }}>← ZUM RAUM</Link>
          <button data-testid="admin-logout-btn" onClick={logout} style={{ ...navItem(false), color: "var(--cs-crimson)" }}>
            <LogOut size={15} /> <span style={{ fontSize: 12 }}>Abmelden</span>
          </button>
        </div>
      </aside>

      {/* content */}
      <main style={{ flex: 1, padding: "28px 34px", overflowY: "auto", height: "100vh" }}>
        {tab === "overview" && <Overview />}
        {tab === "moderation" && <Contributions status="REVIEW_REQUIRED" title="Prüf-Warteschlange" />}
        {tab === "inputs" && <Inputs />}
        {tab === "clusters" && <Clusters />}
        {tab === "manifestations" && <Manifestations />}
        {tab === "reviews" && <Reviews />}
        {tab === "artist" && <ArtistInput />}
        {tab === "settings" && <SettingsTab />}
      </main>
    </div>
  );
}

/* ---------------- Overview ---------------- */
function Overview() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/overview").then((r) => setData(r.data)); }, []);
  if (!data) return <Spinner />;
  return (
    <div>
      <H title="ÜBERSICHT" />
      <div style={grid}>
        <Stat label="Akzeptiert" value={data.contributions.accepted} />
        <Stat label="Prüfung" value={data.contributions.review} accent />
        <Stat label="Abgelehnt" value={data.contributions.rejected} />
        <Stat label="Cluster" value={data.clusters} />
        <Stat label="Beziehungen" value={data.relationships} />
        <Stat label="Veröffentlicht" value={data.manifestations.PUBLISHED} />
        <Stat label="Ausstehend" value={data.manifestations.PENDING_ARTIST_REVIEW} accent />
        <Stat label="Test" value={data.manifestations.TEST} />
      </div>
      <div style={{ marginTop: 26, ...card }}>
        <div className="cs-mono" style={cardHead}>GOOGLE REVIEWS</div>
        <div style={{ fontSize: 13, color: "var(--cs-parchment)" }}>
          Modus: <b style={{ color: data.google_reviews.mode === "LIVE" ? "var(--cs-amber)" : "var(--cs-ash)" }}>{data.google_reviews.mode}</b>
          {data.google_reviews.note && <div className="cs-mono" style={{ fontSize: 10, color: "var(--cs-mute)", marginTop: 6 }}>{data.google_reviews.note}</div>}
        </div>
      </div>
    </div>
  );
}

/* ---------------- Contributions / Inputs ---------------- */
function Inputs() {
  const [status, setStatus] = useState("ACCEPTED");
  return (
    <div>
      <H title="EINGABEN" />
      <div style={{ display: "flex", gap: 8, marginBottom: 18 }}>
        {["ACCEPTED", "REJECTED", "REVIEW_REQUIRED"].map((s) => (
          <button key={s} onClick={() => setStatus(s)} className="cs-btn" style={status === s ? filterActive : filterBtn}>{s}</button>
        ))}
      </div>
      <Contributions status={status} title={null} key={status} />
    </div>
  );
}

function Contributions({ status, title }) {
  const [items, setItems] = useState(null);
  const load = useCallback(() => {
    api.get(`/admin/contributions?status=${status}`).then((r) => setItems(r.data));
  }, [status]);
  useEffect(load, [load]);

  const moderate = async (id, newStatus) => {
    await api.post(`/admin/contributions/${id}/moderate`, { status: newStatus });
    toast.success(newStatus === "ACCEPTED" ? "Akzeptiert" : "Abgelehnt");
    load();
  };

  if (!items) return <Spinner />;
  return (
    <div>
      {title && <H title={title} />}
      {items.length === 0 && <Empty text="Keine Beiträge." />}
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {items.map((c) => (
          <div key={c.id} style={card}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 16 }}>
              <div style={{ flex: 1 }}>
                <p style={{ fontSize: 14, color: "var(--cs-bone)", lineHeight: 1.5 }}>„{c.raw_text}"</p>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 10 }}>
                  <Badge>{c.source_type}</Badge>
                  {(c.concepts || []).map((x) => <Badge key={x} amber>{x}</Badge>)}
                  {(c.moderation?.flags || []).map((f) => <Badge key={f} crimson>{f}</Badge>)}
                </div>
              </div>
              {status !== "ACCEPTED" && (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <button data-testid="admin-approve-thought-btn" onClick={() => moderate(c.id, "ACCEPTED")} style={iconBtn("var(--cs-amber)")}><Check size={15} /></button>
                  <button data-testid="admin-reject-thought-btn" onClick={() => moderate(c.id, "REJECTED")} style={iconBtn("var(--cs-crimson)")}><X size={15} /></button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Clusters ---------------- */
function Clusters() {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = useCallback(() => api.get("/admin/clusters").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const manifest = async (id, test) => {
    setBusy(id + test);
    toast(test ? "Test-Manifestation wird erzeugt (Bild ~1 Min) …" : "Manifestation wird erzeugt …");
    try {
      await api.post(`/admin/clusters/${id}/manifest`, { test }, { timeout: 180000 });
      toast.success("Manifestation erzeugt → Manifestationen-Tab");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(null); load(); }
  };

  if (!items) return <Spinner />;
  return (
    <div>
      <H title="SEMANTISCHE CLUSTER" />
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {items.map((c) => {
          const m = c.measures || {};
          return (
            <div key={c.id} style={card}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
                <div>
                  <div className="cs-heading" style={{ fontSize: 15, color: "var(--cs-bone)" }}>{c.name}</div>
                  <Badge style={{ marginTop: 8 }} amber={c.ready} crimson={!c.ready}>{c.ready ? "BEREIT" : "NICHT BEREIT"}</Badge>
                  {!c.ready && <span className="cs-mono" style={{ fontSize: 9, color: "var(--cs-mute)", marginLeft: 8 }}>{c.reasons.join(" · ")}</span>}
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button data-testid="admin-test-manifestation-btn" disabled={busy} onClick={() => manifest(c.id, true)} className="cs-btn" style={btnGhost}>
                    {busy === c.id + true ? <Loader2 size={12} className="animate-spin" /> : <><Play size={12} style={{ display: "inline", marginRight: 6 }} />Test</>}
                  </button>
                  <button data-testid="admin-create-manifestation-btn" disabled={busy || !c.ready} onClick={() => manifest(c.id, false)} className="cs-btn" style={{ ...btnPrimary, opacity: c.ready ? 1 : 0.4 }}>
                    Manifestieren
                  </button>
                </div>
              </div>
              <div style={{ display: "flex", gap: 18, flexWrap: "wrap", marginTop: 14 }}>
                <Mini label="BEITRÄGE" v={m.meaningful_contribution_count} />
                <Mini label="KOHÄRENZ" v={m.semantic_coherence} />
                <Mini label="SPANNUNG" v={m.tension} />
                <Mini label="QUELLEN" v={m.distinct_sources} />
                <Mini label="BEREITSCHAFT" v={c.readiness_score} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ---------------- Manifestations ---------------- */
function Manifestations() {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("");
  const load = useCallback(() => {
    const q = filter ? `?status=${filter}` : "";
    api.get(`/admin/manifestations${q}`).then((r) => setItems(r.data));
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, ok) => { try { await fn(); toast.success(ok); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };

  const replaceImage = (id) => {
    const inp = document.createElement("input");
    inp.type = "file"; inp.accept = "image/*";
    inp.onchange = () => {
      const f = inp.files[0]; if (!f) return;
      const reader = new FileReader();
      reader.onload = () => act(() => api.post(`/admin/manifestations/${id}/replace-image`, { image: reader.result }), "Bild ersetzt");
      reader.readAsDataURL(f);
    };
    inp.click();
  };
  const editText = (m) => {
    const t = window.prompt("Manifestations-Text bearbeiten:", m.text);
    if (t != null) act(() => api.patch(`/admin/manifestations/${m.id}`, { text: t }), "Text aktualisiert");
  };
  const copy = (obj, msg) => { navigator.clipboard.writeText(typeof obj === "string" ? obj : JSON.stringify(obj, null, 2)); toast.success(msg); };

  if (!items) return <Spinner />;
  return (
    <div>
      <H title="MANIFESTATIONEN" />
      <div style={{ display: "flex", gap: 8, marginBottom: 18, flexWrap: "wrap" }}>
        {["", "TEST", "PENDING_ARTIST_REVIEW", "PUBLISHED", "HIDDEN"].map((s) => (
          <button key={s} onClick={() => setFilter(s)} className="cs-btn" style={filter === s ? filterActive : filterBtn}>{s || "ALLE"}</button>
        ))}
      </div>
      {items.length === 0 && <Empty text="Keine Manifestationen." />}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 16 }}>
        {items.map((m) => (
          <div key={m.id} style={{ ...card, padding: 0, overflow: "hidden" }}>
            <div style={{ aspectRatio: "16/10", background: "#060605" }}>
              {m.image ? <img src={m.image} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> :
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}><span className="cs-mono" style={{ fontSize: 9, color: "var(--cs-mute)" }}>KEIN BILD</span></div>}
            </div>
            <div style={{ padding: 16 }}>
              <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
                <Badge amber={m.status === "PUBLISHED"} crimson={m.status === "HIDDEN"}>{m.status}</Badge>
                {m.is_test && <Badge>TEST</Badge>}
                {m.tattoo_potential && <Badge amber>TATTOO</Badge>}
              </div>
              <p style={{ fontSize: 13, color: "var(--cs-parchment)", fontStyle: "italic", lineHeight: 1.5 }}>„{m.text}"</p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 14 }}>
                <ActBtn tid="admin-publish-manifestation-btn" icon={Send} label="Publish" onClick={() => act(() => api.post(`/admin/manifestations/${m.id}/publish`), "Veröffentlicht")} />
                <ActBtn icon={RefreshCw} label="Regen" onClick={() => act(() => api.post(`/admin/manifestations/${m.id}/regenerate`, {}, { timeout: 180000 }), "Neu erzeugt")} />
                <ActBtn icon={Upload} label="Bild" onClick={() => replaceImage(m.id)} />
                <ActBtn icon={PenLine} label="Text" onClick={() => editText(m)} />
                <ActBtn icon={Star} label="Tattoo" onClick={() => act(() => api.post(`/admin/manifestations/${m.id}/tattoo-potential`, { value: !m.tattoo_potential }), "Aktualisiert")} />
                <ActBtn icon={Eye} label="Hide" onClick={() => act(() => api.post(`/admin/manifestations/${m.id}/hide`), "Versteckt")} />
                <ActBtn icon={Trash2} label="Del" onClick={() => act(() => api.delete(`/admin/manifestations/${m.id}`), "Gelöscht")} />
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                <button onClick={() => copy(m.brief, "Brief kopiert")} className="cs-btn" style={{ ...btnGhost, fontSize: 9, padding: "6px 10px" }}><Copy size={10} style={{ display: "inline", marginRight: 4 }} />Artistic Brief</button>
                <button onClick={() => copy(m.full_image_prompt || m.image_prompt, "Prompt kopiert")} className="cs-btn" style={{ ...btnGhost, fontSize: 9, padding: "6px 10px" }}><Copy size={10} style={{ display: "inline", marginRight: 4 }} />Image Prompt</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Reviews ---------------- */
function Reviews() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/admin/reviews").then((r) => setData(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const sync = async () => {
    setBusy(true);
    try { const { data: r } = await api.post("/admin/reviews/sync", {}, { timeout: 120000 }); toast.success(`${r.imported} Reviews importiert (${r.mode})`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  if (!data) return <Spinner />;
  return (
    <div>
      <H title="GOOGLE REVIEWS" />
      <div style={{ ...card, display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
        <div>
          <div className="cs-mono" style={{ fontSize: 11, color: "var(--cs-parchment)" }}>MODUS: {data.status.mode}</div>
          {data.status.note && <div className="cs-mono" style={{ fontSize: 9, color: "var(--cs-mute)", marginTop: 4 }}>{data.status.note}</div>}
        </div>
        <button data-testid="admin-sync-reviews-btn" onClick={sync} disabled={busy} className="cs-btn" style={btnPrimary}>
          {busy ? <Loader2 size={12} className="animate-spin" /> : <><RefreshCw size={12} style={{ display: "inline", marginRight: 6 }} />Sync</>}
        </button>
      </div>
      {data.reviews.length === 0 && <Empty text="Noch keine Reviews importiert." />}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {data.reviews.map((r) => (
          <div key={r.id} style={card}>
            <p style={{ fontSize: 13, color: "var(--cs-bone)" }}>„{r.raw_text}"</p>
            <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
              <Badge>★ {r.review_meta?.rating ?? "-"}</Badge>
              {(r.concepts || []).map((c) => <Badge key={c} amber>{c}</Badge>)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Artist Input ---------------- */
function ArtistInput() {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try { const { data } = await api.post("/admin/artist-input", { text: text.trim() }); toast.success(`Aufgenommen → ${data.cluster || "neuer Cluster"}`); setText(""); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div>
      <H title="ARTIST INPUT" />
      <div style={{ ...card, maxWidth: 620 }}>
        <p style={{ fontSize: 12, color: "var(--cs-ash)", marginBottom: 14 }}>
          Gedanken, Themen, Fragmente oder künstlerische Notizen. Werden semantisch verarbeitet (Quelle: ARTIST_INPUT).
        </p>
        <textarea data-testid="artist-input-textarea" value={text} onChange={(e) => setText(e.target.value)} maxLength={500} rows={4} className="cs-input" style={{ width: "100%", padding: 14, fontSize: 14, resize: "none" }} placeholder="Ein Impuls für den Raum …" />
        <button data-testid="artist-input-submit-btn" onClick={submit} disabled={busy} className="cs-btn" style={{ ...btnPrimary, marginTop: 12 }}>
          {busy ? <Loader2 size={12} className="animate-spin" /> : "In den Raum geben"}
        </button>
      </div>
    </div>
  );
}

/* ---------------- Settings ---------------- */
function SettingsTab() {
  const [cfg, setCfg] = useState(null);
  const load = useCallback(() => api.get("/admin/project-settings").then((r) => setCfg(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const save = async (patch, msg) => {
    try { const { data } = await api.patch("/admin/project-settings", patch); setCfg(data); toast.success(msg || "Gespeichert"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const toggleFlag = async (flag) => {
    const next = { ...cfg.feature_flags, [flag]: !cfg.feature_flags[flag] };
    save({ feature_flags: next }, "Feature aktualisiert");
  };
  if (!cfg) return <Spinner />;

  return (
    <div style={{ maxWidth: 680 }}>
      <H title="PROJEKT-EINSTELLUNGEN" />

      <div style={card}>
        <div className="cs-mono" style={cardHead}>ÖFFENTLICHE FRAGE</div>
        <input defaultValue={cfg.public_input_prompt} className="cs-input" style={{ width: "100%", padding: 12, fontSize: 14 }}
          onBlur={(e) => e.target.value !== cfg.public_input_prompt && save({ public_input_prompt: e.target.value })}
          data-testid="settings-prompt-input" />
      </div>

      <div style={{ ...card, marginTop: 16 }}>
        <div className="cs-mono" style={cardHead}>MANIFESTATIONS-SCHWELLEN</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          {Object.entries(cfg.manifestation_thresholds).map(([k, v]) => (
            <label key={k} style={{ fontSize: 11, color: "var(--cs-ash)" }}>
              <div className="cs-mono" style={{ fontSize: 9, marginBottom: 4 }}>{k}</div>
              <input type="number" step="0.05" defaultValue={v} className="cs-input" style={{ width: "100%", padding: 10, fontSize: 13 }}
                onBlur={(e) => save({ manifestation_thresholds: { ...cfg.manifestation_thresholds, [k]: parseFloat(e.target.value) } })} />
            </label>
          ))}
          <label style={{ fontSize: 11, color: "var(--cs-ash)" }}>
            <div className="cs-mono" style={{ fontSize: 9, marginBottom: 4 }}>cooldown_days</div>
            <input type="number" defaultValue={cfg.manifestation_cooldown_days} className="cs-input" style={{ width: "100%", padding: 10, fontSize: 13 }}
              onBlur={(e) => save({ manifestation_cooldown_days: parseInt(e.target.value, 10) })} />
          </label>
        </div>
      </div>

      <div style={{ ...card, marginTop: 16 }}>
        <div className="cs-mono" style={cardHead}>VISUAL DNA — PROMPT-PRÄAMBEL</div>
        <textarea defaultValue={cfg.visual_dna?.prompt_preamble} rows={4} className="cs-input" style={{ width: "100%", padding: 12, fontSize: 13, resize: "none" }}
          data-testid="settings-visual-dna-input"
          onBlur={(e) => save({ visual_dna: { ...cfg.visual_dna, prompt_preamble: e.target.value } }, "Visual DNA gespeichert")} />
      </div>

      <div style={{ ...card, marginTop: 16 }}>
        <div className="cs-mono" style={cardHead}>FEATURE FLAGS & SYSTEM</div>
        {Object.entries(cfg.feature_flags).map(([k, v]) => (
          <div key={k} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid var(--cs-border)" }}>
            <span style={{ fontSize: 13, color: "var(--cs-parchment)" }}>{k}</span>
            <button
              data-testid={k === "public_input_enabled" ? "admin-system-pause-switch" : `flag-${k}`}
              onClick={() => toggleFlag(k)}
              style={{ width: 46, height: 24, borderRadius: 12, border: "1px solid var(--cs-border)", background: v ? "var(--cs-amber)" : "#1c1c19", position: "relative", cursor: "pointer", transition: "background-color 0.3s ease" }}>
              <span style={{ position: "absolute", top: 2, left: v ? 24 : 2, width: 18, height: 18, borderRadius: "50%", background: "#0A0A09", transition: "left 0.3s ease" }} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- shared bits ---------------- */
const Spinner = () => <div style={{ padding: 40, textAlign: "center" }}><Loader2 className="animate-spin" color="#736E63" /></div>;
const H = ({ title }) => <h1 className="cs-heading" style={{ fontSize: 18, color: "var(--cs-bone)", marginBottom: 22, letterSpacing: "0.2em" }}>{title}</h1>;
const Empty = ({ text }) => <p className="cs-mono" style={{ fontSize: 11, color: "var(--cs-mute)", padding: 20 }}>{text}</p>;
function Stat({ label, value, accent }) {
  return <div style={card}><div style={{ fontSize: 30, fontFamily: "Cinzel, serif", color: accent ? "var(--cs-amber)" : "var(--cs-bone)" }}>{value}</div><div className="cs-mono" style={{ fontSize: 9, color: "var(--cs-mute)", marginTop: 4 }}>{label}</div></div>;
}
function Mini({ label, v }) {
  return <div><div className="cs-mono" style={{ fontSize: 8, color: "var(--cs-mute)" }}>{label}</div><div style={{ fontSize: 15, color: "var(--cs-bone)", fontFamily: "Cinzel, serif" }}>{v ?? "-"}</div></div>;
}
function Badge({ children, amber, crimson, style }) {
  const c = amber ? "var(--cs-amber)" : crimson ? "var(--cs-crimson)" : "var(--cs-ash)";
  return <span className="cs-mono" style={{ fontSize: 8, padding: "3px 7px", border: `1px solid ${c}`, color: c, ...style }}>{children}</span>;
}
function ActBtn({ icon: Icon, label, onClick, tid }) {
  return <button data-testid={tid} onClick={onClick} className="cs-btn" style={{ ...btnGhost, fontSize: 9, padding: "6px 9px", display: "inline-flex", alignItems: "center", gap: 4 }}><Icon size={11} />{label}</button>;
}

const sidebar = { width: 210, minWidth: 210, background: "#0d0d0b", borderRight: "1px solid var(--cs-border)", display: "flex", flexDirection: "column", height: "100vh", position: "sticky", top: 0 };
const navItem = (active) => ({ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "11px 20px", background: active ? "rgba(216,158,58,0.08)" : "transparent", border: "none", borderLeft: `2px solid ${active ? "var(--cs-amber)" : "transparent"}`, color: active ? "var(--cs-bone)" : "var(--cs-ash)", cursor: "pointer", textAlign: "left", transition: "background-color 0.2s ease, color 0.2s ease" });
const grid = { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 14 };
const card = { background: "rgba(19,19,17,0.7)", border: "1px solid var(--cs-border)", padding: 18 };
const cardHead = { fontSize: 10, color: "var(--cs-mute)", marginBottom: 12 };
const btnPrimary = { padding: "9px 18px", background: "var(--cs-bone)", color: "#0A0A09", border: "none", cursor: "pointer" };
const btnGhost = { padding: "9px 14px", background: "transparent", color: "var(--cs-parchment)", border: "1px solid var(--cs-border-active)", cursor: "pointer" };
const iconBtn = (c) => ({ width: 34, height: 34, background: "transparent", border: `1px solid ${c}`, color: c, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" });
const filterBtn = { padding: "7px 12px", background: "transparent", color: "var(--cs-mute)", border: "1px solid var(--cs-border)", cursor: "pointer" };
const filterActive = { ...filterBtn, color: "var(--cs-bone)", borderColor: "var(--cs-border-active)", background: "rgba(216,158,58,0.06)" };
